// Which controls the public Trust Center shows. Keep in step with
// regodit-api components/trust-center/service/control_visibility.js, which
// is the one that enforces it.
// A control's live status is "pass", "fail" or "unmapped" (no check is mapped
// to it yet, so there is nothing to pass or fail).
export const CONTROL_VISIBILITY = {
  ok: {
    label: "Show passing controls",
    description: "Only controls that are passing are shown.",
  },
  all_with_status: {
    label: "Show all controls with status",
    description:
      "Every control is shown, with whether it is passing or failing.",
  },
  all_without_status: {
    label: "Show all controls without status",
    description: "Every control is shown, with no status at all.",
  },
};

export const DEFAULT_CONTROL_VISIBILITY = "all_with_status";

// Controls of the frameworks shown on the page (Compliance tab). A control
// that is part of several frameworks stays while any of them is shown.
// Without `frameworks`, or for a control without frameworkIds (an older API
// answer), nothing is taken out. Same rule as the API's public page.
export const controlsOfShownFrameworks = (controls, frameworks) => {
  if (!frameworks) return controls;
  const shown = new Set(
    frameworks.filter((f) => f.isVisible !== false).map((f) => f.id)
  );
  return controls.filter(
    (c) =>
      !Array.isArray(c.frameworkIds) ||
      c.frameworkIds.some((id) => shown.has(id))
  );
};

// What a visitor sees: the controls of the shown frameworks that the
// visibility setting shows, with status set to null wherever it is hidden.
export const visibleControls = (controls, visibility, frameworks) => {
  const inScope = controlsOfShownFrameworks(controls, frameworks);
  switch (visibility) {
    case "ok":
      return inScope.filter((c) => c.status === "pass");
    case "all_without_status":
      return inScope.map((c) => ({ ...c, status: null }));
    default:
      return inScope.map((c) =>
        c.status === "unmapped" ? { ...c, status: null } : c
      );
  }
};

// Headline numbers for a list of controls as a visitor sees it. A "x of y
// passing" figure only makes sense when failing controls are shown too.
export const controlSummary = (controls) => {
  const passing = controls.filter((c) => c.status === "pass").length;
  const failing = controls.filter((c) => c.status === "fail").length;
  if (failing > 0)
    return {
      value: `${passing}/${passing + failing}`,
      label: "Controls passing",
    };
  if (passing > 0) return { value: passing, label: "Controls passing" };
  return { value: controls.length, label: "Controls" };
};
